import {Component, computed, OnDestroy, OnInit, signal} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {Observable, of, Subscription} from "rxjs";
import {catchError, map, switchMap, tap} from "rxjs/operators";
import * as _ from "lodash";
import {BaseComponent} from "../base.component";
import {UserService} from "../common/user.service";
import {LoggerService} from "../common/logger.service";
import {GqlCartProductService} from "../product/graphql/gql-cart-product.service";
import {IProduct, IProductGroup} from "../product/product-models";
import {INavDisplay, ITag} from "../cart/catalog/catalog-models";
import {CartUtil} from "../cart/cart-util";
import {Constants} from "../constants";
import {LocaleSwitcherService} from "../locale/locale-switcher.service";
import {CmsContentStackLocaleService, ContentStackLoadingStatus} from '@usana/ux/cms';
import {IShopUser} from "../session/customer-models";
import {LocaleUtil} from "../util/locale-util";

/**
 * PDP page for a pre-made MHP pack (MarketProduct with mhpPrebuiltPackId set), reachable at
 * /pack/:packId instead of /product/:usanaId - all packs use the same ERP product/usanaId 107.xxxxxx,
 * so it doesn't work via normal pdp which needs distinct usanaIds per product for
 * SEO redirects, reviews, recommendations, JSON-LD/OG tags and analytics
 */
@Component({
    selector: 'pack',
    templateUrl: './pack.component.html',
    styleUrls: ['./pack.component.scss'],
    standalone: false
})
export class PackComponent extends BaseComponent implements OnInit, OnDestroy {

    productGroup: IProductGroup;
    product: IProduct;
    isLoading: boolean = false;
    notFound: boolean = false;
    bodyBenefitTags: INavDisplay[] = [];

    showAddToAo: boolean = false;
    showAddToCart: boolean = false;
    selectedProductOption: 'addToAo' | 'addToCart' = 'addToCart';

    useCms = signal<boolean>(false);
    cmsStatus = signal<ContentStackLoadingStatus>(ContentStackLoadingStatus.LOADING);
    showLegacyPdp = computed(() => !this.useCms() || this.cmsStatus() === ContentStackLoadingStatus.NOT_FOUND);

    private routeParamsSubscription: Subscription;

    constructor(
        private readonly gqlCartProductService: GqlCartProductService,
        private readonly route: ActivatedRoute,
        private readonly localeSwitcherService: LocaleSwitcherService,
        private readonly cmsLocaleService: CmsContentStackLocaleService,
        userService: UserService,
        loggerService: LoggerService
    ) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.routeParamsSubscription = this.route.paramMap.pipe(
            map(params => Number(params.get('packId'))),
            // reset on every emission, router reuses this component for navigations between two /pack/:packId routes,
            // so a later packId does not render the previous pack's content while it's still loading
            tap(() => this.isLoading = true),
            // switchMap drops a still-loading previous pack, so its late response can't overwrite this one
            switchMap((packId: number) => this.loadPack(packId))
        ).subscribe((productGroup: IProductGroup) => this.onPackLoaded(productGroup));
    }

    ngOnDestroy() {
        this.routeParamsSubscription?.unsubscribe();
    }

    onCmsStatus(status: ContentStackLoadingStatus) {
        // Show the legacy pack layout if not found
        this.cmsStatus.set(status);
        this.log.debug('CMS status:', status);
    }

    openLocaleSwitcher() {
        this.localeSwitcherService.openLocaleSwitcher();
    }

    // priced by cart-services from the pack's pills (MhpPackPricer), the same price the pack's cart/auto-order
    // line gets - no price means it couldn't be priced right now, so the price block is hidden rather than showing 0
    get hasPackPrice(): boolean {
        return !!this.product?.price;
    }

    get priceDisplay(): string {
        return this.product?.price?.priceDisplay ?? '';
    }

    get aoPriceDisplay(): string {
        return this.product?.aoPrice?.priceDisplay ?? '';
    }

    get volume(): number {
        return this.product?.price?.volume ?? 0;
    }

    // aoDiscount is negative (aoPrice = price + aoDiscount), and so is its display, eg "-25,600원"
    get aoDiscountDisplay(): string {
        return this.product?.aoPrice?.aoDiscount < 0 ? this.product.aoPrice.aoDiscountDisplay : '';
    }

    private loadPack(packId: number): Observable<IProductGroup | null> {
        if (!packId) {
            return of(null);
        }
        this.onCmsStatus(ContentStackLoadingStatus.LOADING);
        return this.gqlCartProductService.getCartPackPDP(packId).pipe(
            catchError((err: unknown) => {
                this.log.error("PackComponent: getCartPackPDP error", err);
                return of(null);
            })
        );
    }

    private onPackLoaded(productGroup: IProductGroup | null): void {
        // gateway's CartProductGroup.products resolver returns an array, packs only have one dummy product in it
        // mirrors ProductRouteGuard's handling minus the usanaId matching (packs have no usanaId to match against).
        const products: IProduct[] = productGroup?.products ? Object.values(productGroup.products) : [];
        const product = products.length > 0 ? products[0] : null;

        if (!productGroup || !product) {
            // the router reuses this component between /pack/:packId routes - drop the previous pack, or its buy box
            // would still render (and could add it) alongside the not-found message
            this.productGroup = undefined;
            this.product = undefined;
            this.bodyBenefitTags = [];
            this.notFound = true;
            this.isLoading = false;
            return;
        }

        this.productGroup = productGroup;
        this.product = product;
        this.notFound = false;
        this.setBodyBenefits();
        this.applyCmsFeatures();
        this.isLoading = false;
    }

    private applyCmsFeatures(): void {
        this.getCustomerData().then((user: IShopUser) => {
            const locale = user?.iso2Locale ?? Constants.DEFAULT_LOCALE;
            // Needs to be set before this.useCms is set to true.
            this.cmsLocaleService.setLocale(CmsContentStackLocaleService.transformIsoCode(LocaleUtil.getUrlLocale(locale)));
            this.useCms.set(user?.config?.useCms);

            // packs have no shopModes of their own (no erp product), so - unlike a regular product's
            // CartUtil.showAddToAoButtonProduct check - this is gated purely on the user's own AO capability
            this.showAddToAo = this.canEditAutoOrderOnline || this.canAddAutoOrderOnline;
            // same check the buy box's pack button uses - like the regular PDP, only offer the one-time option to a
            // shopper who can add to the cart, or an auto-order-only session could pick it and lose the add button
            this.showAddToCart = this.showCart
                && !!user?.shopModes?.some(mode => mode === Constants.SHOP || mode === Constants.ENROLLMENT);
            if (this.showAddToAo) {
                this.selectedProductOption = 'addToAo';
            } else if (this.showAddToCart) {
                this.selectedProductOption = 'addToCart';
            }
        });
    }

    private setBodyBenefits(): void {
        this.bodyBenefitTags = _.filter(this.product.productTags, (tag: ITag) => {
            return !!tag.pdpDisplay && !!tag.parent && Constants.BODY_BENEFIT_TAG_NAME == tag.parent.name;
        }).map(tag => CartUtil.buildNavDisplay(tag));
    }
}
